<template>
  <app-page class="loc-page" fill lean>
    <!-- Kopf wie die übrigen Verwaltungsseiten (Kundenwunsch 13.10.2026): leichter Titel. Ohne Filter und Gruppierung
         stehen die Aktionen mit Icon und Beschriftung rechts in der Werkzeugleiste neben der Suche („damit dort kein
         leerer platz ist“, wie Live-Tracking); Handy: Icon-Knöpfe im Kopf -->
    <template v-if="handy" #actions>
      <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
      <app-icon-button icon="upload" label="Orte aus Excel importieren" @click="dialog = true" />
      <app-icon-button icon="download" label="Excel-Export" @click="exportToExcel" />
      <app-button v-if="hasPermission('REST_LOCATION_POST')" icon="plus" @click="addLocation">Ort anlegen</app-button>
    </template>
    <template #toolbar>
      <app-search v-model="searchText" placeholder="In Orten suchen" @input="onSearchInput" />
    </template>
    <template v-if="!handy" #toolbar-end>
      <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
      <app-button icon="upload" title="Orte aus Excel importieren" @click="dialog = true">Import</app-button>
      <app-button icon="download" @click="exportToExcel">Excel-Export</app-button>
      <app-button v-if="hasPermission('REST_LOCATION_POST')" icon="plus" @click="addLocation">Ort anlegen</app-button>
    </template>
    <input
      type="file"
      ref="fileInput"
      style="display: none"
      accept=".xls, .xlsx"
      v-on:change="onFileUploadChanged($event) "
    />
    <!-- Dialog vor dem Grid: die Speichern-Leiste bleibt letztes Element (unten gerundet wie das Blatt) -->
    <app-dialog v-model="dialog" title="Orte aus Excel importieren">
      Wenn Sie Orte aus einer Excel Datei importieren, werden bereits vorhandene Daten überschrieben. Wollen Sie fortfahren?
      <template #actions>
        <app-button @click="dialog = false">Abbrechen</app-button>
        <app-button kind="danger" @click="dialog = false; $refs.fileInput.click();">Importieren …</app-button>
      </template>
    </app-dialog>
    <!-- Speichern-Leiste über der Tabelle (Kundenwunsch 13.10.2026: „lieber über der tabelle“) -->
    <app-save-bar v-if="hasChanges" top @save="saveChanges" @discard="revertChanges" />
    <!-- Tabelle füllt die Höhe über der Fußleiste (Handy: natürliche Höhe) -->
    <div class="app-grid-fill loc-grid" :class="{ 'loc-grid--auto': handy }">
      <dx-data-grid
        id="locationDataGrid"
        :ref="gridRefName"
        :height="gridHeight"
        :data-source="devicesDataSource"
        :show-borders="false"
        :show-column-lines="false"
        :show-row-lines="true"
        :column-auto-width="true"
        :allow-column-reordering="!gridTouch"
        :allow-column-resizing="!gridTouch"
        :selection="selection"
        :hover-state-enabled="!gridTouch"
        :element-attr="gridAttr"
        @toolbar-preparing="onToolbarPreparing"
        @editing-start="onEditingStart"
        @row-inserting="onDataChanged"
        @row-updating="onDataChanged"
        @row-removing="onDataChanged"
        @content-ready="onContentReady"
        :scrolling="scrolling"
      >
        <dx-group-panel :visible="false" />
        <dx-editing
          refresh-mode="full"
          :allow-adding="false"
          :allow-updating="hasPermission('REST_LOCATION_PUT')"
          :allow-deleting="hasPermission('REST_LOCATION_DELETE')"
          mode="batch"
        />
        <dx-state-storing :enabled="false" type="localStorage" storage-key="storagelocations" />

        <dx-grouping :auto-expand-all="autoExpandAll" expandMode="rowClick" />
        <dx-paging :page-size="defaultPageSize" />
        <!-- Seitenwahl und Blättern in der Fußleiste (AppGridFooter) -->
        <dx-pager :visible="false" />
        <dx-search-panel :visible="false" />
        <dx-export :enabled="false" />

        <!-- Columns -->
        <dx-column
          data-field="location_id"
          caption="Ort-Nummer"
          edit-cell-template="locationIdEditor"
        ></dx-column>
        <dx-column data-field="stop" caption="Haltepunkt"></dx-column>
        <dx-column data-field="name" caption="Bezeichnung" edit-cell-template="locationNameEditor"></dx-column>
        <dx-column data-field="info" caption="Info"></dx-column>
        <dx-column data-field="comb_id" caption="Wabe"></dx-column>
        <dx-column data-field="tariff_zone_1" caption="Tarifzone 1"></dx-column>
        <dx-column data-field="global_id" caption="Globale ID"></dx-column>
        <dx-column
          data-field="gps_longitude_to"
          caption="GPS Länge"
          data-type="number"
          format="##.#########"
          alignment="left"
        ></dx-column>
        <dx-column
          data-field="gps_latitude_to"
          caption="GPS Breite"
          data-type="number"
          format="##.#########"
          alignment="left"
        ></dx-column>
        <dx-column
          data-field="passenger_display_name"
          caption="Fahrgastanzeige"
        ></dx-column>

        <!-- Column Edit Templates -->
        <template #locationIdEditor="cellInfo">
          <DxAutocomplete
            :value.sync="locationId"
            value-expr="location_id"
            item-template="locationIdTemplate"
            :data-source="locationIdDataSource"
            :on-item-click="(value) => onItemClick(value, cellInfo)"
            @value-changed="(value) => onValueChanged(value, cellInfo)"
            :wrap-item-text="true"
          >
            <template #locationIdTemplate="location">
              <span>{{ location.data.location_id }}, {{ location.data.name }}, {{ location.data.stop_name }}</span>
            </template>
          </DxAutocomplete>
        </template>

        <template #locationNameEditor="cellInfo">
          <DxAutocomplete
            :value.sync="locationName"
            value-expr="display_name"
            item-template="locationNameTemplate"
            :data-source="locationNameDataSource"
            :on-item-click="(value) => onItemClick(value, cellInfo)"
            @value-changed="(value) => onValueChanged(value, cellInfo)"
            :wrap-item-text="true"
          >
            <template #locationNameTemplate="location">
              <span>{{ location.data.location_id }}, {{ location.data.name }}, {{ location.data.stop_name }}</span>
            </template>
          </DxAutocomplete>
        </template>
      </dx-data-grid>
    </div>
    <app-grid-footer ref="fuss" :page-sizes="pageSizes">
      <template #summary>
        <span v-if="narrowed"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }}</span>
        <span v-else><strong>{{ summaryCount }}</strong> {{ summaryNoun }}</span>
      </template>
    </app-grid-footer>
  </app-page>
</template>

<script>
import {
  DxDataGrid,
  DxPaging,
  DxSearchPanel,
  DxPager,
  DxExport,
  DxColumn,
  DxGrouping,
  DxGroupPanel,
  DxEditing,
  DxStateStoring
} from "devextreme-vue/data-grid";
import { DxAutocomplete } from "devextreme-vue";
import deMessages from "devextreme/localization/messages/de.json";
import { locale, loadMessages } from "devextreme/localization";
import CustomStore from "devextreme/data/custom_store";
import axios from "axios";
import ExcelJS from "exceljs";
import { hasPermission } from "@/auth/auth";
import DataGridMixin from "@/mixins/DataGridMixin";
import { formatNumber, plural } from "@/utils/format";

export default {
  components: {
    DxDataGrid,
    DxPaging,
    DxSearchPanel,
    DxPager,
    DxExport,
    DxColumn,
    DxGrouping,
    DxGroupPanel,
    DxEditing,
    DxStateStoring,
    DxAutocomplete
  },
  mixins: [DataGridMixin],
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: AppPage fill wirkt dort nicht, das Grid wächst mit seinen Zeilen; sonst füllt es die Höhe
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    // Fußleiste: Anzahl nach der Suche, „4 von 37 Orten“ solange eingeschränkt
    narrowed() {
      return this.summary.count !== this.summary.loaded;
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded) + " " + plural(this.summary.loaded, "Ort", "Orten");
    },
    summaryNoun() {
      return plural(this.summary.count, "Ort", "Orte");
    }
  },
  created() {
    loadMessages(deMessages);
    locale("de"); // immer Deutsch wie src/plugins/devextreme.js (vorher Browsersprache)
  },
  data() {
    return {
      locationId: "",
      locationName: "",
      customer: this.$store.state.customerId,
      user: this.$store.state.cognitoUsername,
      menu: false,
      modal: false,
      gridRefName: "locationDataGrid",
      // Grid-Optionen als Objekte in data (nicht als Literal im Template, Spezifikation 1.3 Nr. 8)
      selection: { mode: "single" },
      gridAttr: {},
      search: "",
      searchText: "",
      hasChanges: false,
      dialog: false,
      autoExpandAll: false,
      summary: { count: 0, loaded: 0 },
      devicesDataSource: new CustomStore({
        key: ["location_id", "stop"],
        load: () =>
          axios.get(
            `${this.$store.state.baseUrl}location?customer_id=${this.$store.state.customerId}`
          ).then(response => {
            this._rows = Array.isArray(response.data) ? response.data : [];
            return response;
          }),
        insert: newLocation =>
          axios.post(`${this.$store.state.baseUrl}location`, [newLocation]),
        update: (keys, updates) =>
          axios.put(
            `${this.$store.state.baseUrl}location?location_id=${keys.location_id}&stop=${keys.stop}`,
            updates
          ),
        remove: keys =>
          axios.delete(
            `${this.$store.state.baseUrl}location?location_id=${keys.location_id}&stop=${keys.stop}`
          )
      }),
      locationIdDataSource: new CustomStore({
        load: loadOptions => {
          const locationId = loadOptions.searchValue;
          return axios.get(
            `${this.$store.state.baseUrl}globallocation?location_id=${locationId}`
          );
        }
      }),
      locationNameDataSource: new CustomStore({
        load: loadOptions => {
          const name = loadOptions.searchValue;
          return axios.get(
            `${this.$store.state.baseUrl}globallocation?name=${name}`
          );
        }
      })
    };
  },

  methods: {
    onToolbarPreparing(e) {
      // Remove all default toolbar items; die leere Leiste ganz ausblenden (sonst zweite Linie über dem Tabellenkopf)
      e.toolbarOptions.items = [];
      e.toolbarOptions.visible = false;
    },
    onContentReady(e) {
      const grid = e.component;
      const count = Number(grid.totalCount());
      this.summary = { count: count >= 0 ? count : 0, loaded: (this._rows || []).length };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
    },
    onEditingStart(e) {
      this.locationId = e.data.location_id || "";
      this.locationName = e.data.name || "";
      // Nur setzen wenn es eine tatsächliche Bearbeitung ist
      if (e && e.data) {
        this.hasChanges = true;
      }
    },
    onDataChanged(e) {
      // Nur setzen wenn es eine tatsächliche Datenänderung ist
      if (e && e.data) {
        this.hasChanges = true;
      }
    },
    onSearchInput() {
      this.$refs[this.gridRefName].instance.searchByText(this.searchText || "");
    },
    addLocation() {
      this.$refs[this.gridRefName].instance.addRow();
    },
    async saveChanges() {
      await this.$refs[this.gridRefName].instance.saveEditData();
      this.hasChanges = false;
    },
    revertChanges() {
      this.$refs[this.gridRefName].instance.cancelEditData();
      this.hasChanges = false;
    },
    exportToExcel() {
      this.$refs[this.gridRefName].instance.exportToExcel(false);
    },
    hasPermission(permissionName) {
      return hasPermission(permissionName);
    },
    onItemClick(e, cellInfo) {
      const index = cellInfo.data.row.key.rowIndex || cellInfo.data.rowIndex;
      cellInfo.data.component.cellValue(
        index,
        "location_id",
        e.itemData.location_id
      );
      cellInfo.data.component.cellValue(index, "name", e.itemData.display_name);
      cellInfo.data.component.cellValue(index, "stop", e.itemData.stop);
      cellInfo.data.component.cellValue(
        index,
        "global_id",
        e.itemData.global_id
      );
      cellInfo.data.component.cellValue(
        index,
        "gps_latitude_to",
        e.itemData.latitude
      );
      cellInfo.data.component.cellValue(
        index,
        "gps_longitude_to",
        e.itemData.longitude
      );
      // cellInfo.data.component.updateDimensions();
    },
    onValueChanged(value, cellInfo) {
      this.location_id = value;
      cellInfo.data.setValue(value.value);
    },
    refreshDataGrid() {
      this.$refs[this.gridRefName].instance.refresh();
    },
    onFileUploadChanged(e) {
      const selectedFile = e.target.files[0];

      const wb = new ExcelJS.Workbook();
      const reader = new FileReader();
      const rows = [];

      reader.readAsArrayBuffer(selectedFile);
      reader.onload = () => {
        const buffer = reader.result;
        wb.xlsx.load(buffer).then(workbook => {
          workbook.eachSheet(async sheet => {
            sheet.eachRow(row => {
              const data = {
                location_id: row.values[1],
                stop: row.values[2],
                name: row.values[3],
                info: row.values[4],
                comb_id: row.values[5],
                tariff_zone_1: row.values[6],
                global_id: row.values[7],
                gps_longitude_to: row.values[8],
                gps_latitude_to: row.values[9],
                passenger_display_name: row.values[10] || null
              };
              rows.push(data);
            });
            rows.shift();
            await axios.post(`${this.$store.state.baseUrl}location`, rows);
            this.refreshDataGrid();
          });
        });
      };
    }
  }
};
</script>

<style scoped>
/* Handy: Grid in natürlicher Höhe */
.loc-grid.loc-grid--auto {
  flex: none;
}
</style>
